import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ShowcaseProductCard from "@/components/ShowcaseProductCard";
import ShowcaseProductModal from "@/components/ShowcaseProductModal";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Package } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShowcaseCategoryPage() {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchCategoryData();
  }, [categoryId]);

  const fetchCategoryData = async () => {
    setLoading(true);
    try {
      // First get the category (can be by slug or id)
      const categoryRes = await axios.get(`${API}/showcase/categories/${categoryId}`);
      const categoryData = categoryRes.data;
      setCategory(categoryData);
      
      // Then get products using the category's actual ID
      const productsRes = await axios.get(`${API}/showcase/products?category_id=${categoryData.id}`);
      setProducts(productsRes.data);
    } catch (error) {
      console.error("Error fetching category data:", error);
      // Try to fetch by finding in all categories if direct lookup failed
      try {
        const allCategoriesRes = await axios.get(`${API}/showcase/categories`);
        const foundCategory = allCategoriesRes.data.find(
          c => c.slug === categoryId || c.id === categoryId
        );
        if (foundCategory) {
          setCategory(foundCategory);
          const productsRes = await axios.get(`${API}/showcase/products?category_id=${foundCategory.id}`);
          setProducts(productsRes.data);
        }
      } catch (innerError) {
        console.error("Error in fallback fetch:", innerError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProduct(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="text-xl text-muted-foreground">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Header */}
      <section className="bg-gradient-to-br from-primary/10 to-secondary/10 border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
          <Link to="/#product-categories">
            <Button variant="ghost" className="mb-4 -ml-2 gap-2">
              <ChevronLeft className="h-4 w-4" />
              Back to Categories
            </Button>
          </Link>
          {category ? (
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              {category.image && (
                <div className="w-24 h-24 rounded-lg overflow-hidden bg-white border border-border/50 flex-shrink-0">
                  <img 
                    src={category.image} 
                    alt={category.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope' }} data-testid="category-heading">
                  {category.name}
                </h1>
                <p className="text-muted-foreground">
                  {products.length} product{products.length !== 1 ? 's' : ''} available
                </p>
              </div>
            </div>
          ) : (
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Category Not Found
            </h1>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {products.length === 0 ? (
            <div className="text-center py-20">
              <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Products Yet</h3>
              <p className="text-muted-foreground mb-4">Products in this category will appear here once added.</p>
              <Link to="/">
                <Button variant="outline">Back to Home</Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <ShowcaseProductCard
                  key={product.id}
                  product={product}
                  onClick={handleProductClick}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Product Detail Modal */}
      <ShowcaseProductModal
        product={selectedProduct}
        open={modalOpen}
        onClose={handleCloseModal}
      />

      <Footer />
    </div>
  );
}
